import React, { useEffect, useRef } from 'react';
import { ComplianceCheck } from '../../utils/types';
import EnforcementRefBlock from '../EnforcementRefBlock';
import EvidenceChips from './EvidenceChips';
import { crosscheckState, isDisputed } from './crosscheck/state';
import {
  ConfirmedChip,
  DisputedChip,
  DisputedExplainLink,
  TwoReadings,
  yourCallText,
} from './crosscheck/CrosscheckParts';
import { SECTION_META } from '../../designTokens';
import { FixGroup, issueCardId } from './fixGroups';
import { splitFixSteps } from './fixSteps';

interface IssueCardProps {
  check: ComplianceCheck;
  onOpenEvidence?: (ref: { section: string; filter?: string }) => void;
  /** The FixList group (#1660); picks the warning stripe and the confirm badge. */
  group?: FixGroup;
}

const DISPUTED_FINDING = 'Our two readings of your privacy policy disagree on this disclosure.';

/**
 * One labelled row of the card: the label in a left gutter (stacked above
 * the body below 640px). Labels are h4s so a screen reader can jump between
 * Why it matters, How to fix and Evidence.
 */
function Row({ label, ai, children }: { label: string; ai?: boolean; children: React.ReactNode }) {
  return (
    <div className={`issue-card__row${ai ? ' issue-card__row--ai' : ''}`}>
      <h4 className="issue-card__row-label">{label}</h4>
      <div className="issue-card__row-body">{children}</div>
    </div>
  );
}

/**
 * The fix itself. On a disputed card it collapses behind "If the passage
 * falls short" (#1659), since the passage may already cover it; the
 * disclosure opens for print so paper shows the fix too.
 */
function FixBlock({ collapsed, children }: { collapsed: boolean; children: React.ReactNode }) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (!collapsed) return;
    const openForPrint = () => {
      if (ref.current) ref.current.open = true;
    };
    window.addEventListener('beforeprint', openForPrint);
    return () => window.removeEventListener('beforeprint', openForPrint);
  }, [collapsed]);

  if (collapsed) {
    return (
      <details ref={ref} className="issue-card__fix issue-card__fix-disclosure">
        <summary className="issue-card__fix-toggle">If the passage falls short</summary>
        {children}
      </details>
    );
  }
  return <div className="issue-card__fix">{children}</div>;
}

/** Summary in bold, then numbered steps, then lettered alternatives (fixSteps.ts). */
function FixContent({ check }: { check: ComplianceCheck }) {
  if (!check.fix) return <p className="issue-card__fix-summary">{check.recommendation}</p>;
  const { steps, options } = splitFixSteps(check.fix.steps ?? []);
  return (
    <>
      <p className="issue-card__fix-summary">{check.fix.summary}</p>
      {steps.length > 0 && (
        <ol className="issue-card__fix-steps">
          {steps.map((step, idx) => (
            <li key={idx}>{step}</li>
          ))}
        </ol>
      )}
      {options.length > 0 && (
        <ul className="issue-card__fix-options">
          {options.map((option) => (
            <li key={option.letter}>
              <span className="issue-card__option-name">
                Option {option.letter} · {option.name}
                {option.qualifier && <span className="issue-card__option-qualifier">{option.qualifier}</span>}
              </span>
              {option.text}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * An issue card answers three questions in order (DESIGN.md "Issue card"):
 * the finding as one lede sentence, then labelled rows for Why it matters
 * (with the legal basis), How to fix, and Evidence. Proof comes last because
 * it supports the finding; it no longer stands between the reader and the fix.
 */
export default function IssueCard({ check, onOpenEvidence, group }: IssueCardProps) {
  const cleanSeverity = check.severity.toLowerCase();
  const isScoredFailure = check.status === 'non_compliant';
  const isConfirm = group === 'confirm';
  // Warnings get a slate stripe by group (#1660) so they don't read as HIGH's orange.
  const groupModifier = group === 'recommended' || isConfirm ? ` check-card--${group}` : '';
  const cardClass = `check-card issue-card check-card--${isScoredFailure ? cleanSeverity : 'warn'}${groupModifier}`;
  const badgeModifier = isConfirm ? 'confirm' : isScoredFailure ? cleanSeverity : 'warning';
  const badgeText = isConfirm
    ? 'Check'
    : isScoredFailure ? check.severity : (check.status === 'warning' ? 'WARNING' : check.severity);

  // AI Crosscheck as two readings (#1659). The severity badge and stripe keep
  // meaning severity; the crosscheck is carried by its own violet chip.
  const adjudication = check.adjudication;
  const xcheck = crosscheckState(adjudication);
  const disputed = isDisputed(xcheck);

  // Per-check capability detection, never version switching: a v2 report
  // carries details/description/recommendation instead of finding/fix.
  const isStructured = Boolean(check.finding || check.fix || (check.evidenceItems && check.evidenceItems.length > 0));
  const patternText = check.finding || check.details;
  const area = check.section ? (SECTION_META[check.section]?.label ?? check.section) : null;
  const hasFix = Boolean(check.fix || check.recommendation);
  const citation = check.ccpaReference ? (
    <span className="issue-card__citation">CCPA {check.ccpaReference}</span>
  ) : null;

  return (
    <article id={issueCardId(check)} tabIndex={-1} className={cardClass}>
      <div className="issue-card__body">
        <div className="issue-card__title-row">
          <h3 className="issue-card__title" style={{ margin: 0 }}>{check.name}</h3>
          <span className={`severity-badge severity-badge--${badgeModifier}`}>{badgeText}</span>
          {disputed && <DisputedChip />}
          {xcheck === 'confirmed' && <ConfirmedChip />}
          {area && <span className="issue-card__area">{area}</span>}
        </div>

        {disputed ? (
          <p className="issue-card__finding">{DISPUTED_FINDING}</p>
        ) : (
          patternText && <p className="issue-card__finding">{patternText}</p>
        )}
        {!isStructured && check.description && <p className="issue-card__description">{check.description}</p>}

        <div className="issue-card__rows">
          {disputed && adjudication && (
            <Row label="Two readings">
              <TwoReadings adjudication={adjudication} patternFound={xcheck === 'disputed-passed'} patternText={patternText} />
              <DisputedExplainLink />
            </Row>
          )}
          {/* It asks the reader to read the quote, so no quote, no row. */}
          {xcheck === 'disputed-failed' && adjudication?.quote && (
            <Row label="Your call" ai>
              <p className="issue-card__text">{yourCallText(check.fix?.coverage)}</p>
            </Row>
          )}
          {(xcheck === 'confirmed' || xcheck === 'corrected-fail') && (
            <Row label="AI Crosscheck" ai>
              <p className="issue-card__text">
                <span className="issue-card__verdict">
                  {xcheck === 'confirmed' ? 'Agrees with the pattern check.' : 'Corrected the verdict.'}
                </span>
                {adjudication?.reasoning && <> <span>{adjudication.reasoning}</span></>}
              </p>
            </Row>
          )}

          {check.whyItMatters ? (
            <Row label="Why it matters">
              <p className="issue-card__text">{check.whyItMatters}</p>
              {citation && <p className="issue-card__legal">Legal basis {citation}</p>}
            </Row>
          ) : (
            citation && (
              <Row label="Legal basis">
                <p className="issue-card__text">{citation}</p>
              </Row>
            )
          )}

          {hasFix && (
            <Row label="How to fix">
              <FixBlock collapsed={disputed}>
                <FixContent check={check} />
              </FixBlock>
            </Row>
          )}

          {check.evidenceItems && check.evidenceItems.length > 0 && (
            <Row label="Evidence">
              <EvidenceChips items={check.evidenceItems} onOpenEvidence={onOpenEvidence} />
            </Row>
          )}
        </div>
      </div>

      {check.enforcementReferences && check.enforcementReferences.length > 0 && (
        <div className="issue-card__enforcement">
          <EnforcementRefBlock refs={check.enforcementReferences} />
        </div>
      )}
    </article>
  );
}
